export type AuthEntryMode = 'pro' | 'channel' | 'office';
export type TokenEntryMode = 'remote' | 'secure' | 'link' | 'share';

/** AccessType credentialing/backend-service value for each token-based mode. */
export const TOKEN_ACCESS_TYPE: Record<TokenEntryMode, string> = {
  secure: '40',
  link: '41',
  remote: '42',
  share: '45',
};

export interface TokenRouteMatch {
  mode: TokenEntryMode;
  token: string;
  routePath: string;
}

export interface AuthEntryConfig {
  mode: AuthEntryMode;
  routePath: string;
  endpointPath: '' | '/channel' | '/office';
  accessType: '10' | '20' | '30';
  requiresPassword: boolean;
  requiresTwoFactorCode: boolean;
  passkeyEnabled: boolean;
  policyNotice?: string;
}

const AUTH_ENTRY_CONFIGS: Record<AuthEntryMode, AuthEntryConfig> = {
  pro: {
    mode: 'pro',
    routePath: '/pro',
    endpointPath: '',
    accessType: '10',
    requiresPassword: false,
    requiresTwoFactorCode: true,
    passkeyEnabled: true,
  },
  channel: {
    mode: 'channel',
    routePath: '/channel',
    endpointPath: '',
    accessType: '20',
    requiresPassword: true,
    requiresTwoFactorCode: true,
    passkeyEnabled: false,
  },
  office: {
    mode: 'office',
    routePath: '/office',
    endpointPath: '/office',
    accessType: '30',
    requiresPassword: false,
    requiresTwoFactorCode: true,
    passkeyEnabled: true,
  },
};

export const resolveAuthEntryModeFromPath = (pathName: string): AuthEntryMode => {
  const normalized = pathName.trim().toLowerCase();
  if (normalized.startsWith('/channel')) {
    return 'channel';
  }
  if (normalized.startsWith('/office')) {
    return 'office';
  }
  return 'pro';
};

/** Default SkinType ("Patient Tools") for /pro, /channel (Account), /office, and any unrecognized path. */
export const DEFAULT_SKIN_TYPE = '3';

/** Branded sign-in routes that override the default SkinType for WebSignInView. */
const SKIN_TYPE_BY_ROUTE: Record<string, string> = {
  psychscan: '1',
  childnav:  '2',
  pti:       '3',
  comt:      '5',
  carengrow: '6',
};

export const resolveSkinTypeFromPath = (pathName: string): string => {
  const normalized = pathName.trim().toLowerCase();
  const route = Object.keys(SKIN_TYPE_BY_ROUTE).find((r) => normalized.startsWith(`/${r}`));
  return route ? SKIN_TYPE_BY_ROUTE[route] : DEFAULT_SKIN_TYPE;
};

export const resolveTokenRoute = (pathName: string): TokenRouteMatch | null => {
  const normalized = pathName.trim();
  const match = normalized.match(/^\/(remote|secure|link|share)\/([^/?#]+)/i);
  if (!match) {
    return null;
  }

  const mode = match[1].toLowerCase() as TokenEntryMode;
  const token = decodeURIComponent(match[2]);
  return {
    mode,
    token,
    routePath: `/${mode}/${match[2]}`,
  };
};

export const getAuthEntryConfig = (mode: AuthEntryMode): AuthEntryConfig => AUTH_ENTRY_CONFIGS[mode];

export const getDefaultAuthRoute = (): string => AUTH_ENTRY_CONFIGS.pro.routePath;
